import _ from '../../utils/underscore';
import BaseService from '../../base/base_service';
import Event from '../../utils/event';
import {SYSTEM_EVENTS} from '../../events/events';
import IAnalyticsService, { AnalyticsServiceInitParams, IAnalyticsData } from './analytics_service_interface';
import {IElasticEventData, IStructuredLogger} from '../../../../common/interfaces/structured-logging-service-interface';

const GTM_URL = 'www.googletagmanager.com/gtm.js';
const PROD_DATA_LAYER_NAME = 'dataLayer';

class AnalyticsService extends BaseService implements IAnalyticsService{

    static CLASS: string = 'AnalyticsService';
    private _initParams: AnalyticsServiceInitParams = null;
    private _GTMDataLayer: any = null;

    private _isInitialisedByB2C: boolean = false;

    init(params: AnalyticsServiceInitParams) {
        super.init(params);
        this._setInitParams(params);
        this._subscribe();
    }

    private _setInitParams(params: AnalyticsServiceInitParams): void {
        this._initParams = {
            GTMContainerID: params.GTMContainerID,
            UATrackingID: params.UATrackingID,
            GTMDataLayerName: params.GTMDataLayerName
        };
        this._initGTMDataLayer();
    }

    private _initGTMDataLayer(): void {
        this._GTMDataLayer = {
            cgpIsAuthenticated: false,
            cgpIsRegistered: false,
            cgpCID: undefined,
            cgpIsFTD: false,
            cgpPlayerStatus: undefined,
            cgpLoginSessionID: undefined
        };
    }


    private _loadGTM(document: Document, dataLayerName: string, GTMContainerId: string): void {
        this.pushDataToDataLayer({'gtm.start': new Date().getTime(), 'event': 'gtm.js'});

        const firstScript: HTMLScriptElement = document.getElementsByTagName("script")[0];

        const newScript: HTMLScriptElement = document.createElement("script");
        const dataLayer: string = dataLayerName !== PROD_DATA_LAYER_NAME ? '&l=' + dataLayerName : '';
        newScript.async = true;
        newScript.src = '//' + GTM_URL + '?id=' + GTMContainerId + dataLayer;
        firstScript.parentNode.insertBefore(newScript, firstScript);
    }


    /**
     * Gathering analytic data from CGP
     *
     * @returns {*}
     * @private
     */
    private _getCGPEventData(): any {
        this._initGTMDataLayer();
        // tslint:disable-next-line:no-console
        console.log("PASSED");
        const dataLayer = this._GTMDataLayer;

        // get authorized data
        if (this.serviceFactory.oAuth2Service().isAuthorized()) {
            // authorization data
            dataLayer.cgpLoginSessionID = +this.serviceFactory.oAuth2Service().getToken().sessionId;

            // user service data
            const accountData = this.serviceFactory.userService().getAccountData();
            if (accountData) {
                dataLayer.cgpCID = accountData.Cid;
                dataLayer.cgpIsVip = accountData.IsVip;
            }

            // b2c data
            const b2cData = this._getB2CData();
            if (b2cData && b2cData.sessionParamsMsg) {
                dataLayer.cgpPlayerStatus = b2cData.sessionParamsMsg.PlayerStatus;
                dataLayer.cgpIsFTD = b2cData.sessionParamsMsg.IsFTD;
                dataLayer.cgpIsRegistered = b2cData.sessionParamsMsg.IsRegistered;
                dataLayer.cgpIsAuthenticated = b2cData.sessionParamsMsg.IsAuthenticated;
            }
        }

        dataLayer.cgpExperiment = undefined;

        return dataLayer;
    }

    /**
     * Extract b2c data
     * @returns {any}
     * @private
     */
    private _getB2CData(): any {
        const b2cData = this.serviceFactory.oAuth2Service().extractTokenProperty('b2c');
        if (b2cData) {
           return JSON.parse(b2cData);
        }
    }

    /**
     * Temp function to select B2C dataLayer if initialized before our dataLayer (Use their over ours)
     */
    _selectPlatformDataLayerIfExists() {
        if(window[PROD_DATA_LAYER_NAME]) {
            this._initParams.GTMDataLayerName = PROD_DATA_LAYER_NAME;
            this._isInitialisedByB2C = true;
        }
    }

    /**
     * initialization of datalayer and GTM
     * @private
     */
    private _initGTM(): void {
        const dataLayerName = this._initParams.GTMDataLayerName;

        window[dataLayerName] = window[dataLayerName] || [];
        this.pushDataToDataLayer(this._createDataLayer());

        if (!this._isInitialisedByB2C) {
            this._loadGTM(document, this._initParams.GTMDataLayerName, this._initParams.GTMContainerID);
        }
    }


    private _subscribe(): void {
        this.onSystemEvent(SYSTEM_EVENTS.ON_INIT_COMPLETE, () => {
            this._selectPlatformDataLayerIfExists();
            this._initGTM();
        });

        this.onSystemEvent(SYSTEM_EVENTS.OAUTH_STATE_CHANGE, () => {
            this.pushDataToDataLayer(this._createDataLayer());
        });

        this.onSystemEvent(SYSTEM_EVENTS.ANALYTIC_TRIGGER_ACTIVATED, (data) => {
            this._sendAnalytics(data);
        });
    }


    private _sendAnalytics(data: IAnalyticsData): void {
        const dataToSend = Object.assign({},
            {
                event: data.eventName || 'CGP_GENERIC_EVENT',
                CGP_EventCategory: data.category,
                CGP_EventAction: data.action,
                CGP_EventLabel: data.label,
                CGP_EventValue: data.value
            });

        if (!data.omitStructuredLogger) {
            this._sendAnalyticsToElasticSearch(data);
        }

        this.pushDataToDataLayer(dataToSend);
        this._resetDataLayer();
    }

    private _sendAnalyticsToElasticSearch(data: IAnalyticsData) {
        const dataToSend: IElasticEventData = {
            CGPEventCategory: data.category?.toString(),
            CGPEventAction: data.action?.toString(),
            CGPEventLabel: data.label?.toString(),
            CGPEventValue: data.value?.toString()
        };

        if (!_.isNullOrUndefined(dataToSend.CGPEventValue)) {
            const valueAsNumber = (dataToSend.CGPEventValue as any) * 1;
            if (_.isNumber(valueAsNumber)) {
                dataToSend.CGPEventValueAsNumber = valueAsNumber;
            }
        }

        this.serviceFactory.structuredLoggingService().getLogger(dataToSend).info();
    }

    /**
     * Clear GTM dataLayer for avoid memory leak
     * GTM persists dataLayer properties, so in order to send each time the new data, we have to reset dataLayer at all, otherwise we should provide
     * undefined value for each property we don't want to send, for example user properties if they are not exist anymore
     * @private
     */
    private _resetDataLayer(): void {
        this._getDataLayerObject().pop();
        // window.google_tag_manager[this._model.getGTMContainerID()].dataLayer.reset();
    }

    private _getDataLayerObject(){
        return window[this._initParams.GTMDataLayerName] || {pop: () =>{} , push : ()=>{}};
    }


    /**
     * Get data layer object to be send
     * @returns {{} & any & any}
     * @private
     */
    private _createDataLayer(): any {
        return Object.assign({},
            {
                cgpUATrackingID: this._initParams.UATrackingID,
            },
            this._getCGPVersionInfo(),
            this._getCGPEventData());
    }


    private _getCGPVersionInfo(): any {
        const skinService = this.serviceFactory.skinService();
        return {
            cgpProductPackage: skinService.getProductPackageID(),
            cgpVersion: skinService.getVersion()
        };
    }

    public sendEvent(analyticsData: IAnalyticsData): void {
        Event.fire(SYSTEM_EVENTS.ANALYTIC_TRIGGER_ACTIVATED, analyticsData);
    }

    public sendClickstreamEvent(analyticsData: IAnalyticsData):void{
        try {
            const logger: IStructuredLogger = this.serviceFactory.structuredLoggingService()
                .getLogger()
                .addCategory(analyticsData.category)
                .addAction(analyticsData.action)
                .addLabel(analyticsData.label)
                .addValue(analyticsData.value);

            logger.setEventType(analyticsData.eventName,'Log', 'Analytics');
            logger.info();
        }
        catch (err){
            console.log("Error on CGP sendOrbitUserFlowEvent : " + err);
        }
    }

    private _getGoogleSessionObject() {
        const ga = this.serviceFactory.windowService().getGlobalProperty('ga');
        if (ga && _.isFunction(ga.getAll)) {
            return ga.getAll()[0] || {
                get: () => {}
            };
        } else {
            return {
                get: () => {}
            }
        }

    }

    public getGoogleSessionID(): string {
        return this._getGoogleSessionObject().get('linkerParam') || '';
    }

    public getGoogleClientID(): string {
        return this._getGoogleSessionObject().get('clientId') || '';
    }

    public pushDataToDataLayer(data:{}):void {
        this._getDataLayerObject().push(data);
    }

}

export default AnalyticsService;